// Hand-off from the directory claim flow.
//
// The claim success page on expyhealth.com (and the approval email) link to
// /providers/apply?npi=<registry id>&country=<US|GB> so the application starts
// linked to the listing the therapist just claimed. The id is a US NPI (ten
// digits) or a UK HCPC registration (PH plus digits); both are public registry
// data, which is why they may ride a URL while the email never does (it is
// typed on the start screen). The API validates both again, seeds a new draft
// with them, and copies the identity it already holds for that email.
//
// Fail-safe: anything missing or malformed is simply no hand-off, and the
// screen behaves exactly as a plain visit.

export type HandoffCountry = 'US' | 'GB';

export interface ClaimHandoff {
  npi: string;
  country: HandoffCountry;
}

const NPI_RE = /^\d{10}$/;
const HCPC_RE = /^PH\d{4,8}$/i;

export function parseClaimHandoff(search: string): ClaimHandoff | null {
  const params = new URLSearchParams(search);
  const npi = (params.get('npi') || '').trim().toUpperCase();
  if (!NPI_RE.test(npi) && !HCPC_RE.test(npi)) return null;
  const rawCountry = (params.get('country') || '').trim().toUpperCase();
  // The id already says which market it is from; an explicit country only has
  // to agree with it. Anything else falls back to what the id implies.
  const implied: HandoffCountry = HCPC_RE.test(npi) ? 'GB' : 'US';
  const country: HandoffCountry = rawCountry === 'US' || rawCountry === 'GB' ? rawCountry : implied;
  return { npi, country };
}

export function readClaimHandoff(): ClaimHandoff | null {
  try {
    return parseClaimHandoff(window.location.search);
  } catch {
    return null;
  }
}

// "NPI 1234567890" or "HCPC PH123456", for copy that names the listing.
export function registryLabel(handoff: ClaimHandoff): string {
  return `${handoff.country === 'GB' ? 'HCPC' : 'NPI'} ${handoff.npi}`;
}
